/// <reference path="./globals.d.ts" />
import { AIRequestType, AIResponseType, MessageSenderType } from '../types/Message';
import { getWelcomeContainer } from './state';
import { sendMessage } from './websocket';

// ─── DOM Element References ───────────────────────────────────────────────────

export const chatMessages = document.getElementById('chat-messages') as HTMLDivElement;
export const chatForm = document.getElementById('chat-form') as HTMLFormElement;
export const chatInput = document.getElementById('chat-input') as HTMLTextAreaElement;
export const sendBtn = document.getElementById('send-btn') as HTMLButtonElement;
export const stopBtn = document.getElementById('stop-btn') as HTMLButtonElement;
export const statusDot = document.getElementById('status-dot') as HTMLSpanElement;
export const statusText = document.getElementById('status-text') as HTMLSpanElement;
export const connectionBanner = document.getElementById('connection-banner') as HTMLDivElement;
export const typingIndicator = document.getElementById('typing-indicator-container') as HTMLDivElement;
export const thinkingLabel = document.getElementById('thinking-label') as HTMLSpanElement;
export const clearChatBtn = document.getElementById('clear-chat-btn') as HTMLButtonElement;
export const fullscreenBtn = document.getElementById('fullscreen-btn') as HTMLButtonElement;
export const suggestionChips = document.querySelectorAll('.suggestion-chip') as NodeListOf<HTMLButtonElement>;

// ─── Message Rendering ────────────────────────────────────────────────────────

class MessageBubble {

    wrapper = document.createElement('div');

    contents: {
        text: string;
        type?: string;
    }[] = [];

    private _render: () => HTMLDivElement[];

    constructor(public sender: MessageSenderType, public id: string) {
        this.wrapper.classList.add('message-wrapper', sender);
        this._render = (this as any)['render_' + this.sender];
    }

    addContent(text: string, type?: string): void {
        const last = this.contents[this.contents.length - 1];
        if (last && last.type == type) {
            last.text += text;
        } else {
            this.contents.push({ text, type });
        }
    }

    clear() {
        this.contents.length = 0;
    }

    detech() {
        this.wrapper.remove();
    }

    render(): void {
        this.wrapper.replaceChildren.apply(this.wrapper, this._render());
    }

    private render_user = (): HTMLDivElement[] => {
        const bubble = document.createElement('div');
        bubble.classList.add('message-bubble');
        this.contents.forEach(content => {
            const p = document.createElement('p');
            p.textContent = content.text;
            content.type && p.classList.add(content.type);
            bubble.appendChild(p);
        })
        return [bubble];
    }

    private render_ai = (): HTMLDivElement[] => {
        const bubble = document.createElement('div');
        bubble.classList.add('message-bubble');

        const copyBtn = document.createElement('button');
        copyBtn.className = 'copy-btn';
        copyBtn.title = 'Copy message';
        copyBtn.innerHTML = '<i class="fa-regular fa-copy"></i>';
        copyBtn.onclick = () => {
            navigator.clipboard.writeText(this.contents.map(content => content.text).join('\n')).then(() => {
                copyBtn.innerHTML = '<i class="fa-solid fa-check" style="color: var(--success);"></i>';
                setTimeout(() => {
                    copyBtn.innerHTML = '<i class="fa-regular fa-copy"></i>';
                }, 2000);
            });
        };
        bubble.appendChild(copyBtn);
        this.contents.forEach(content => {
            const div = document.createElement('div');
            content.type && div.classList.add(content.type);
            div.innerHTML += marked.parse(content.text);
            bubble.appendChild(div);
        })
        removeMessageBubble('thinking');
        return [bubble];
    }

    private render_thinking = (): HTMLDivElement[] => {
        return this.contents.map(content => {
            const badge = document.createElement('div');
            badge.className = `system-badge ${content.type}`;
            badge.innerHTML = `<span>${content.text}</span>`;
            return badge;
        })
    }

    private render_error = (): HTMLDivElement[] => {
        return this.contents.map(content => {
            const badge = document.createElement('div');
            badge.className = `system-badge ${content.type}`;
            badge.innerHTML = `<i class="fa-solid fa-triangle-exclamation"></i><span>${content.text}</span>`;
            return badge;
        })
    }


    private addTimestamp(bubble: HTMLDivElement): void {
        const meta = document.createElement('div');
        meta.classList.add('message-meta');
        meta.textContent = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        bubble.appendChild(meta);
    }
}

const bubbles: Partial<Record<MessageSenderType, MessageBubble>> = {};

function removeMessageBubble(sender: MessageSenderType): void {
    const bubble = bubbles[sender];
    if (bubble) {
        bubble.detech();
    }
}

export function getSenderTypeByAIResponseType(responseType: AIResponseType): MessageSenderType {
    switch (responseType) {
        case 'error':
            return 'error';
        case 'reasoning':
            return 'thinking';
        default:
            return 'ai';
    }
}

export function getMessageBubble(id: string, sender: MessageSenderType): MessageBubble {
    const welcome = getWelcomeContainer();
    if (welcome) {
        welcome.remove();
    }

    let current = bubbles[sender];
    if (current && current.id !== id) {
        if (current.sender == 'thinking') {
            current.detech();
        }
        current = null!;
    }
    if (current) {
        if (current.wrapper.parentElement?.lastElementChild !== current.wrapper) {
            current.detech();
            chatMessages.appendChild(current.wrapper);
        }
    } else {
        current = new MessageBubble(sender, id);
        bubbles[sender] = current;
        chatMessages.appendChild(current.wrapper);
    }
    scrollToBottom();
    return current;
}

export function releaseMessageBubble(sender: MessageSenderType, id?: string): void {
    const bubble = bubbles[sender];
    if (bubble) {
        if (typeof id !== 'string' || bubble.id === id) {
            delete bubbles[sender];
        }
    }
}

export function restoreWelcomeMessage(sendMessage: (type: AIRequestType, content: string) => void): void {
    // Remove all messages and reset state
    const chatMessages = document.getElementById('chat-messages') as HTMLDivElement;
    chatMessages.innerHTML = '';
    typingIndicator.classList.add('hidden');

    const welcomeTemplate = document.getElementById('welcome-template') as HTMLTemplateElement;
    const welcomeContainer = welcomeTemplate.content.cloneNode(true) as HTMLDivElement;
    if (welcomeContainer && welcomeTemplate) {
        chatMessages.appendChild(welcomeContainer);
        // Re-bind suggestion chip listeners on the recreated welcome panel
        const newChips = welcomeContainer.querySelectorAll('.suggestion-chip') as NodeListOf<HTMLButtonElement>;
        newChips.forEach(chip => {
            chip.addEventListener('click', () => sendMessage('requestAIResponse', chip.textContent));
        });
    }
}

// ─── Typing Indicator ─────────────────────────────────────────────────────────

export function showTypingIndicator(label: string, requestID?: string): void {
    // Strip surrounding brackets from thinking labels e.g. "[Searching]"
    thinkingLabel.textContent = label.replace(/[\[\]]/g, '');
    typingIndicator.classList.remove('hidden');
    scrollToBottom();
    if (requestID) {
        stopBtn.classList.remove('hidden');
        stopBtn.onclick = () => {
            sendMessage('stopAIResponse', requestID);
        }
    } else {
        stopBtn.classList.add('hidden');
    }
}

export function hideTypingIndicator(): void {
    typingIndicator.classList.add('hidden');
}

// ─── Scroll ───────────────────────────────────────────────────────────────────

export function scrollToBottom(): void {
    chatMessages.scrollTop = chatMessages.scrollHeight;
}

// ─── Input Control ────────────────────────────────────────────────────────────

export function disableInput(): void {
    chatInput.disabled = true;
    sendBtn.disabled = true;
}

export function enableInput(): void {
    chatInput.disabled = false;
    sendBtn.disabled = chatInput.value.trim() === '';
}

export function resizeInput(): void {
    chatInput.style.height = 'auto';
    const scrollHeight = chatInput.scrollHeight;

    if (scrollHeight > 120) {
        chatInput.style.height = '120px';
        chatInput.style.overflowY = 'auto';
    } else {
        chatInput.style.height = `${scrollHeight}px`;
        chatInput.style.overflowY = 'hidden';
    }
}

// ─── Fullscreen Toggle ────────────────────────────────────────────────────────

let isFullscreen = false;

export function toggleFullscreen(): void {
    const widget = document.querySelector('.chat-widget') as HTMLDivElement;
    if (!widget) return;

    isFullscreen = !isFullscreen;
    widget.classList.toggle('fullscreen', isFullscreen);
    window.parent.postMessage({
        type: 'fullscreen',
        fullscreen: isFullscreen,
    }, '*')

    // Update icon
    const icon = fullscreenBtn.querySelector('i');
    if (icon) {
        icon.className = isFullscreen ? 'fa-solid fa-compress' : 'fa-solid fa-expand';
    }

    // Update tooltip
    fullscreenBtn.title = isFullscreen ? 'Exit fullscreen' : 'Toggle fullscreen';
}